import { centroid, bbox, point, distance } from '@turf/turf';

/**
 * Получение центра объекта (координаты [lng, lat])
 */
export function getFeatureCenter(feature) {
    if (!feature || !feature.geometry) return [0, 0];
    try {
        if (feature.geometry.type === 'Point') {
            return feature.geometry.coordinates;
        }
        const c = centroid(feature);
        if (c && c.geometry && c.geometry.coordinates) {
            return c.geometry.coordinates;
        }
    } catch (e) {}

    // Fallback: центр BBox
    try {
        const box = bbox(feature);
        return [(box[0] + box[2]) / 2, (box[1] + box[3]) / 2];
    } catch (e) {}

    return [0, 0];
}

/**
 * Смещение геометрии на заданную дельту [deltaLng, deltaLat]
 */
export function translateGeometry(geometry, deltaLng, deltaLat) {
    if (!geometry || !geometry.coordinates) return geometry;

    const shiftCoord = (coord) => {
        if (typeof coord[0] === 'number' && typeof coord[1] === 'number') {
            return [coord[0] + deltaLng, coord[1] + deltaLat];
        }
        if (Array.isArray(coord)) {
            return coord.map(shiftCoord);
        }
        return coord;
    };

    return {
        ...geometry,
        coordinates: shiftCoord(geometry.coordinates)
    };
}

/**
 * Извлечение уникальных вершин контура полигона (без замыкающей точки)
 */
function getPolygonUniqueVertices(geom) {
    if (!geom || !geom.coordinates) return [];
    let ring = [];
    if (geom.type === 'Polygon' && Array.isArray(geom.coordinates[0])) {
        ring = geom.coordinates[0];
    } else if (geom.type === 'MultiPolygon' && Array.isArray(geom.coordinates[0]?.[0])) {
        ring = geom.coordinates[0][0];
    }
    if (ring.length === 0) return [];
    
    // Убираем замыкающую точку, если она совпадает с первой
    const pts = ring.slice(0, ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1] ? -1 : ring.length);
    return pts;
}

/**
 * Расчет точных векторов стыковки граней для 4 сторон (справа, слева, сверху, снизу)
 * Для 4-сторонних участков состыковывает ребро-в-ребро с учетом ориентации
 */
export function compute4BoundaryVectors(feature) {
    if (!feature || !feature.geometry) return null;
    const geom = feature.geometry;
    const pts = getPolygonUniqueVertices(geom);
    
    const box = bbox(feature); // [minX, minY, maxX, maxY]
    const width = box[2] - box[0];
    const height = box[3] - box[1];

    if (pts.length === 4) {
        // Вычисляем центр
        const cLng = (pts[0][0] + pts[1][0] + pts[2][0] + pts[3][0]) / 4;
        const cLat = (pts[0][1] + pts[1][1] + pts[2][1] + pts[3][1]) / 4;
        const latRad = (cLat * Math.PI) / 180;
        const cosLat = Math.cos(latRad);

        // Классифицируем 4 угла по углу относительно центра
        const corners = pts.map(p => {
            const dx = (p[0] - cLng) * cosLat;
            const dy = p[1] - cLat;
            const angle = (Math.atan2(dy, dx) * 180) / Math.PI; // [-180, 180]
            return { p, angle };
        });

        // 4 сектора:
        // TR (Северо-Восток): 0..90
        // TL (Северо-Запад): 90..180
        // BL (Юго-Запад): -180..-90
        // BR (Юго-Восток): -90..0
        let TR = corners.find(c => c.angle >= 0 && c.angle < 90)?.p;
        let TL = corners.find(c => c.angle >= 90 && c.angle <= 180)?.p;
        let BL = corners.find(c => c.angle >= -180 && c.angle < -90)?.p;
        let BR = corners.find(c => c.angle >= -90 && c.angle < 0)?.p;

        if (!TR || !TL || !BL || !BR) {
            corners.sort((a, b) => a.angle - b.angle);
            BR = corners[0].p;
            TR = corners[1].p;
            TL = corners[2].p;
            BL = corners[3].p;
        }

        const rightVec = [
            ((TR[0] - TL[0]) + (BR[0] - BL[0])) / 2,
            ((TR[1] - TL[1]) + (BR[1] - BL[1])) / 2
        ];
        const leftVec = [-rightVec[0], -rightVec[1]];

        const topVec = [
            ((TL[0] - BL[0]) + (TR[0] - BR[0])) / 2,
            ((TL[1] - BL[1]) + (TR[1] - BR[1])) / 2
        ];
        const bottomVec = [-topVec[0], -topVec[1]];

        return { right: rightVec, left: leftVec, top: topVec, bottom: bottomVec };
    }

    // Fallback для полигонов с другим числом вершин или линий
    return {
        right: [width, 0],
        left: [-width, 0],
        top: [0, height],
        bottom: [0, -height]
    };
}

/**
 * Вычисление точной привязки дубликата по 4 сторонам строго к исходному объекту
 * @param {Object} sourceFeature - Исходный объект, с которого делали копию
 * @param {Array<number>} anchorCoord - Исходная точка центра объекта [lng, lat]
 * @param {Array<number>} currentCoord - Текущие координаты курсора [lng, lat]
 * @param {boolean} isShiftActive - Зажат ли Shift
 * @returns {Object} { translatedFeature, isSnapped, snapSide, snapLabel }
 */
export function computeDuplicatePlacement({
    sourceFeature,
    anchorCoord,
    currentCoord,
    isShiftActive = false
}) {
    if (!sourceFeature || !sourceFeature.geometry || !anchorCoord || !currentCoord) {
        return {
            translatedFeature: sourceFeature,
            isSnapped: false,
            snapLine: null,
            snapSide: null,
            snapLabel: null
        };
    }

    const baseDeltaLng = currentCoord[0] - anchorCoord[0];
    const baseDeltaLat = currentCoord[1] - anchorCoord[1];

    // Базовое свободное перемещение без Shift
    if (!isShiftActive) {
        let translatedGeom = translateGeometry(sourceFeature.geometry, baseDeltaLng, baseDeltaLat);
        return {
            translatedFeature: {
                ...sourceFeature,
                id: sourceFeature.id || crypto.randomUUID(),
                geometry: translatedGeom
            },
            isSnapped: false,
            snapLine: null,
            snapSide: null,
            snapLabel: null
        };
    }

    // Получаем точные векторы смещения для 4 сторон
    const boundaryVectors = compute4BoundaryVectors(sourceFeature);
    if (!boundaryVectors) {
        let translatedGeom = translateGeometry(sourceFeature.geometry, baseDeltaLng, baseDeltaLat);
        return {
            translatedFeature: {
                ...sourceFeature,
                id: sourceFeature.id || crypto.randomUUID(),
                geometry: translatedGeom
            },
            isSnapped: false,
            snapLine: null,
            snapSide: null,
            snapLabel: null
        };
    }

    // Переводим вектор смещения в метрические координаты с учетом широты
    const latRad = (anchorCoord[1] * Math.PI) / 180;
    const metersPerDegreeLng = 111320 * Math.cos(latRad);
    const metersPerDegreeLat = 111320;

    const deltaX_meters = baseDeltaLng * metersPerDegreeLng;
    const deltaY_meters = baseDeltaLat * metersPerDegreeLat;

    // Вычисляем угол смещения курсора относительно центра объекта в градусах [-180, 180]
    const angleDeg = (Math.atan2(deltaY_meters, deltaX_meters) * 180) / Math.PI;

    let snapDeltaLng = 0;
    let snapDeltaLat = 0;
    let snapSide = null;
    let snapLabel = null;

    if (angleDeg >= -45 && angleDeg < 45) {
        // 1. СПРАВА (East): левая граница дубликата соединяется со всей правой границей оригинала
        snapDeltaLng = boundaryVectors.right[0];
        snapDeltaLat = boundaryVectors.right[1];
        snapSide = 'right';
        snapLabel = 'Справа (встык)';
    } else if (angleDeg >= 45 && angleDeg < 135) {
        // 2. СВЕРХУ (North): нижняя граница дубликата соединяется со всей верхней границей оригинала
        snapDeltaLng = boundaryVectors.top[0];
        snapDeltaLat = boundaryVectors.top[1];
        snapSide = 'top';
        snapLabel = 'Сверху (встык)';
    } else if (angleDeg >= -135 && angleDeg < -45) {
        // 3. СНИЗУ (South): верхняя граница дубликата соединяется со всей нижней границей оригинала
        snapDeltaLng = boundaryVectors.bottom[0];
        snapDeltaLat = boundaryVectors.bottom[1];
        snapSide = 'bottom';
        snapLabel = 'Снизу (встык)';
    } else {
        // 4. СЛЕВА (West): правая граница дубликата соединяется со всей левой границей оригинала
        snapDeltaLng = boundaryVectors.left[0];
        snapDeltaLat = boundaryVectors.left[1];
        snapSide = 'left';
        snapLabel = 'Слева (встык)';
    }

    const snappedGeom = translateGeometry(sourceFeature.geometry, snapDeltaLng, snapDeltaLat);

    return {
        translatedFeature: {
            ...sourceFeature,
            id: sourceFeature.id || crypto.randomUUID(),
            geometry: snappedGeom
        },
        isSnapped: true,
        snapLine: null,
        snapSide,
        snapLabel
    };
}

/**
 * Создание готового дубликата со скопированными свойствами
 */
export function createDuplicateFeature(sourceFeature, newGeometry) {
    if (!sourceFeature) return null;

    const originalProps = sourceFeature.properties || {};
    const originalName = originalProps.name || 'Объект';
    const newName = originalName.includes('(Копия)') ? originalName : `${originalName} (Копия)`;

    const newProps = {
        ...originalProps,
        name: newName,
        cadastral_number: '', // Очищаем кадастровый номер для предотвращения искусственного дубля
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
    };

    return {
        type: 'Feature',
        id: crypto.randomUUID(),
        geometry: newGeometry || sourceFeature.geometry,
        properties: newProps
    };
}
